import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_TX_CENTER_VALUES,
  EditTXCenterForm,
  useTxCenterFormSchema,
  TXCentersForm,
} from '@lp/features/admin/forms/TXCentersForm';
import { PrimaryTXUserForm } from '@lp/features/admin/forms/PrimaryTXUserForm';
import { AuthContext } from '@lp/core/auth';
import { TXCenter } from '@/api/apis/facilities';
import { useUpdateTXCenterMutation } from '@/queries/tx-centers';
import { useUpdateUserCustomerMutation } from '@/queries/customer';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import {
  convertToIanaTimezone,
  getChangedValues,
  getShortTzByIanaName,
  separateRolesAndPermissions,
} from '@/utils/helpers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { useWrappedRemoveTxCenterMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { CustomerMT } from '@/api/apis/customer';

const isOnlyPrimaryTXUserIdChanged = (changedValues: Partial<EditTXCenterForm>) => {
  return Object.keys(changedValues).length === 1 && 'primaryTXUserId' in changedValues;
};

export const TXCentersEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<TXCenter>();
  const { mutateAsync } = useUpdateTXCenterMutation();
  const { mutateAsync: updateCustomerAsync } = useUpdateUserCustomerMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const changePrimaryTxUser = async (user: CustomerMT | undefined, isPrimary: boolean) => {
    if (user) {
      await updateCustomerAsync({
        id: user.id,
        customerInTxCenters: user.customerInTxCenters.map((item) => {
          if (item.txCenterId === data.id) {
            return {
              ...item,
              isPrimary,
            };
          }
          return item;
        }),
      });
    }
  };

  const hasCreatePermissions = checkPermission(AdminPermissions.TxCenterCreate);
  const { schema, isLoading: isGeoDataLoading } = useTxCenterFormSchema();
  const hasEditNotesPermissions = checkPermission(AdminPermissions.TxCenterUpdateNotes);
  const hasQadCustomerIdPermissions = checkPermission(AdminPermissions.TxCenterUpdateQadCustomerId);

  const defaultValues = useMemo<EditTXCenterForm>(() => {
    const txUsersList = data.txUsers || [];
    const mainTXUser = txUsersList.find((c) =>
      c.customerInTxCenters.find((p) => p.isPrimary && p.txCenterId === data.id),
    );
    const currentRoles = mainTXUser?.customerInTxCenters.find((c) => c.txCenterId === data.id)?.roles;

    const { roles, permissions } = separateRolesAndPermissions(currentRoles);

    return {
      ...DEFAULT_TX_CENTER_VALUES,
      name: data.name,
      code: data.code,
      logoPath: data.logoPath || '',
      timezone: getShortTzByIanaName(data.location.timezone) || '',
      timezoneIANA: data.location.timezone,
      country: data.location.country,
      addressLine: data.location.addressLine,
      city: data.location.city,
      state: data.location.state,
      latitude: data.location.latitude,
      longitude: data.location.longitude,
      postalCode: data.location.postalCode,
      firstName: mainTXUser?.firstName || '',
      lastName: mainTXUser?.lastName || '',
      email: mainTXUser?.email || '',
      primaryPhone: mainTXUser?.primaryPhone || '',
      secondaryPhone: mainTXUser?.secondaryPhone || '',
      primaryTXUserId: mainTXUser?.id || '',
      photoPath: mainTXUser?.photoPath || '',
      roles,
      permissions,
      notes: data.notes || '',
      hasFixedPriceContract: data.hasFixedPriceContract,
      trialProgramIds: data.trialPrograms?.map((i) => i.id) || [],
      trialProgramNotes: data.trialProgramNotes || '',
      qadCustomerId: data.qadCustomerId || null,
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/customers/tx_centers');
  };

  const handleSubmit = async (values: EditTXCenterForm) => {
    try {
      let uploadedFile;

      if (values.image) {
        uploadedFile = await uploadFiles([values.image]);
      }

      const hasTrialPrograms = !!values.trialProgramIds?.length;

      const updateBody = {
        name: values.name,
        code: values.code,
        timezone: values.timezoneIANA || convertToIanaTimezone(values.timezone),
        logoPath: uploadedFile ? uploadedFile[0].fileName : values.logoPath,
        addressLine: values.addressLine,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        longitude: values.longitude,
        latitude: values.latitude,
        primaryTXUserId: values.primaryTXUserId,
        notes: values.notes,
        hasFixedPriceContract: values.hasFixedPriceContract,
        trialProgramIds: hasTrialPrograms ? values.trialProgramIds : null,
        trialProgramNotes: values.trialProgramNotes,
        qadCustomerId: values.qadCustomerId || null,
      };

      const changedValues = getChangedValues<Partial<EditTXCenterForm>>(updateBody, defaultValues);

      if (!isOnlyPrimaryTXUserIdChanged(changedValues)) {
        const { primaryTXUserId, ...updatedValues } = changedValues;
        await mutateAsync({
          id: data.id,
          isOnlyNotesUpdate: !hasCreatePermissions && hasEditNotesPermissions,
          ...updatedValues,
        });
      }

      const primaryUserChanged = changedValues.primaryTXUserId;

      if (primaryUserChanged) {
        const prevMainTXUser = data?.txUsers?.find((c) => c.customerInTxCenters.find((p) => p.isPrimary));
        if (prevMainTXUser) {
          changePrimaryTxUser(prevMainTXUser, false);
        }
        const newTXCenterUser = data?.txUsers?.find((c) => c.id === primaryUserChanged);
        if (newTXCenterUser) {
          changePrimaryTxUser(newTXCenterUser, true);
        }
      }

      toast.success(`TX Center updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, isLoading, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveTxCenterMutation,
    successMessage: 'TX Center deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleTXCenterDelete = () => {
    onModalOpen(data.id);
  };

  if (isGeoDataLoading) return null;

  return (
    <>
      <Form<EditTXCenterForm> defaultValues={defaultValues} schema={schema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit TX Center" backToListLink="/admin/customers/tx_centers">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleTXCenterDelete}
              hasCreatePermissions={hasCreatePermissions}
              hasEditPermissions={hasCreatePermissions || hasEditNotesPermissions || hasQadCustomerIdPermissions}
              hasDeletePermissions={hasCreatePermissions}
            />
          </AdminPageHeader>
          <TXCentersForm
            isReadOnly={!hasCreatePermissions}
            isNotesReadOnly={!hasEditNotesPermissions}
            isQadCustomerIdReadOnly={!hasQadCustomerIdPermissions}
          />
          <PrimaryTXUserForm txCenterId={data.id} list={data.txUsers} isReadOnly isDisabled={!hasCreatePermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete TX Center?"
        description="Are you sure you want to delete the TX Center?"
        isOpen={isOpen}
        isLoading={isLoading}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
